'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import {
  Briefcase,
  Users,
  Mic,
  Video,
  MapPin,
  Award,
  ChevronRight,
  Clock,
} from 'lucide-react';
import { Button as DsButton } from '@/components/ds';
import { useAnalytics } from './providers/AnalyticsProvider';
import { useAuthContext } from './providers/AuthProvider';
import Navbar from './Navbar';

// Re-enable when Indiestry has actual accounts on these platforms.
// Generic platform URLs were misleading users (Reddit feedback, 2026-05).
const SHOW_SOCIAL_LINKS = false;

/* ------------------------------------------------------------------ */
/* Local marketing-only primitives — shared with /explore in spirit,   */
/* defined locally per page since they're not promoted to the ds.      */
/* ------------------------------------------------------------------ */

function Waveform({
  count = 48,
  accent = false,
  height = 56,
  accentRatio = 0.4,
}: {
  count?: number;
  accent?: boolean;
  height?: number;
  accentRatio?: number;
}) {
  const cap = height < 32 ? 28 : 48;
  const bars = Array.from({ length: count }, (_, i) => {
    const baseAmplitude = height < 32 ? 16 : 28;
    const secondAmplitude = height < 32 ? 10 : 18;
    const baseOffset = height < 32 ? 4 : 6;
    const h =
      baseOffset +
      Math.abs(Math.sin(i * 0.7) * baseAmplitude) +
      Math.abs(Math.cos(i * 0.31) * secondAmplitude);
    return Math.min(cap, h);
  });
  const barWidth = height < 32 ? 'w-0.5 rounded-[1px]' : 'w-[3px] rounded-[2px]';
  const gap = height < 32 ? 'gap-[2px]' : 'gap-0.5';
  return (
    <div className={`flex items-center ${gap}`} style={{ height: `${height}px` }} aria-hidden>
      {bars.map((h, i) => {
        const isAccentBar = accent && i / count <= accentRatio;
        return (
          <div
            key={i}
            className={`${barWidth} ${isAccentBar ? 'bg-accent' : 'bg-surface-3'}`}
            style={{ height: `${h}px` }}
          />
        );
      })}
    </div>
  );
}

const ACCENT_GLOW_LG =
  'shadow-[0_0_0_1px_oklch(0.62_0.22_270/0.5),0_12px_32px_oklch(0.62_0.22_270/0.3)]';
const ITALIC_GRADIENT =
  'bg-[linear-gradient(180deg,oklch(0.78_0.18_270)_0%,oklch(0.55_0.24_270)_100%)] bg-clip-text text-transparent';

/* ------------------------------------------------------------------ */
/* Feature card with micro-reveal hover                                */
/* ------------------------------------------------------------------ */

function FeatureCard({
  icon: IconCmp,
  title,
  body,
  detail,
  badge,
}: {
  icon: typeof Briefcase;
  title: string;
  body: string;
  detail: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <div
      aria-disabled
      className="group relative overflow-hidden p-6 bg-surface-2 border border-border-subtle rounded-md cursor-default transition-all duration-200 ease hover:border-accent hover:-translate-y-0.5"
    >
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent opacity-0 -translate-x-1 transition-all duration-200 ease group-hover:opacity-100 group-hover:translate-x-0"
      />
      <div className="w-12 h-12 rounded-[10px] bg-accent-soft text-accent grid place-items-center mb-5">
        <IconCmp className="w-6 h-6" strokeWidth={1.75} />
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <h3 className="m-0 text-lg font-semibold tracking-[-0.005em] text-text-primary">
          {title}
        </h3>
        {badge}
      </div>
      <p className="m-0 mb-4 text-text-secondary text-sm leading-[1.55]">{body}</p>
      <div className="h-9 flex items-center opacity-40 transition-opacity duration-200 ease group-hover:opacity-100">
        {detail}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Per-card detail elements                                            */
/* ------------------------------------------------------------------ */

const Tags = ({ tags }: { tags: string[] }) => (
  <div className="flex flex-wrap gap-1">
    {tags.map((t) => (
      <span
        key={t}
        className="px-2 py-0.5 rounded-full bg-accent-soft text-accent text-[10px] font-medium"
      >
        {t}
      </span>
    ))}
  </div>
);

const Quote = ({ text }: { text: string }) => (
  <p className="m-0 italic text-[12px] leading-[1.4] text-text-secondary">
    &ldquo;{text}&rdquo;
  </p>
);

// Neutral "not yet available" pill — muted tokens so it reads as a roadmap
// signal, not an active feature. Theme-agnostic (Booth/Daylight via tokens).
const ComingSoonBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-3 border border-border-subtle text-text-tertiary text-[10px] font-mono uppercase tracking-wide whitespace-nowrap">
    <Clock className="w-3 h-3" strokeWidth={2} aria-hidden />
    Coming soon
  </span>
);

// Honest detail for not-yet-shipped features, matching the muted detail row.
const InDevelopment = ({ text }: { text: string }) => (
  <span className="inline-flex items-center gap-2 text-[12px] text-text-tertiary">
    <Clock className="w-3.5 h-3.5" strokeWidth={1.75} aria-hidden />
    {text}
  </span>
);

const LocationPill = ({ label }: { label: string }) => (
  <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-surface-3 border border-border-subtle">
    <MapPin className="w-3 h-3 text-accent" strokeWidth={1.75} />
    <span className="text-mono font-mono uppercase text-text-secondary">{label}</span>
  </span>
);

const FOOTER_NAVIGATE = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Jobs', href: '/jobs' },
  { label: 'Community', href: '/explore' },
];

const FOOTER_RESOURCES = [
  { label: 'Help center', href: '/help' },
  { label: 'Contact', href: '/contact' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Blog', href: '/blog' },
];

const FOOTER_LEGAL = [
  { label: 'Terms of service', href: '/terms-of-service' },
  { label: 'Privacy policy', href: '/privacy-policy' },
  { label: 'Community guidelines', href: '/community-guidelines' },
  { label: 'Report issue', href: '/report-issue' },
];

export default function LandingPage() {
  const { trackEvent } = useAnalytics();
  const { user, isAuthenticated, loading } = useAuthContext();
  const router = useRouter();
  const [videoError, setVideoError] = useState(false);

  // Redirect authenticated users to /home
  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      router.replace('/home');
    }
  }, [isAuthenticated, user, loading, router]);

  // Track page view (only for unauthenticated users)
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      trackEvent('page_viewed', {
        page: 'landing_page',
        title: 'Indiestry Landing Page',
      });
    }
  }, [trackEvent, isAuthenticated, loading]);

  if (loading || (isAuthenticated && user)) {
    return null;
  }

  const handleVideoError = () => setVideoError(true);

  const featureCards: Array<{
    icon: typeof Briefcase;
    title: string;
    body: string;
    detail: React.ReactNode;
    badge?: React.ReactNode;
  }> = [
    {
      icon: Briefcase,
      title: 'Voice Acting Jobs',
      body: 'Discover exclusive voice acting opportunities, auditions, and casting calls from top studios and creators.',
      detail: <Tags tags={['audiobook', 'animation', 'lead-role']} />,
    },
    {
      icon: Users,
      title: 'Creative Community',
      body: 'Connect with fellow voice actors, share experiences, and build lasting professional relationships.',
      detail: (
        <Quote text="Just nailed my first paid gig — thanks to feedback from this thread." />
      ),
    },
    {
      icon: Mic,
      title: 'Portfolio Showcase',
      body: 'Showcase your best work with our professional portfolio tools and get discovered by industry professionals.',
      detail: <Waveform count={28} height={28} accent accentRatio={0.5} />,
    },
    {
      icon: Video,
      title: 'Live Streaming',
      body: 'Stream live auditions, workshops, and Q&As. Build your audience in real-time, on your terms.',
      detail: <InDevelopment text="Streaming tools are in development" />,
      badge: <ComingSoonBadge />,
    },
    {
      icon: MapPin,
      title: 'Global Reach',
      body: 'Connect with casting directors and creators worldwide. Your next role might be on the other side of the planet.',
      detail: (
        <div className="flex flex-wrap gap-1.5">
          <LocationPill label="LOS ANGELES" />
          <LocationPill label="TOKYO" />
          <LocationPill label="REMOTE" />
        </div>
      ),
    },
    {
      icon: Award,
      title: 'Professional Growth',
      body: 'Workshops, mentorship, and resources designed by working voice actors to help you level up your craft.',
      detail: (
        <Quote text="Mentor feedback turned my reel from forgettable to bookable." />
      ),
    },
  ];

  return (
    <div data-theme="booth" className="min-h-screen bg-gradient-to-br from-[#0B0B3B] via-[#141E61] to-[#1E3A8A]">
      {/* Navigation */}
      <Navbar />

      {/* Hero Section — UNCHANGED, video bg in production */}
      <section id="hero" className="relative h-screen overflow-hidden pt-20">
        {/* Background gradient - always present */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0B3B] via-[#141E61] to-[#1E3A8A]" />

        {/* Background video - only show if no error */}
        {!videoError && (
          <div className="absolute inset-0 z-10">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover opacity-30"
              poster="/hero-poster.jpg"
              onError={handleVideoError}
            >
              <source src="https://nvlcmbgzysgutotuwrnh.supabase.co/storage/v1/object/public/media//IndiestryBackgroundVideo.mp4" type="video/mp4" />
              <source src="/hero-video.webm" type="video/webm" />
            </video>
          </div>
        )}

        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/30 z-20" />

        {/* Hero content */}
        <div className="relative z-30 text-center text-white flex flex-col items-center justify-center px-4 h-screen">
          <div className="max-w-4xl mx-auto w-full">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold mb-4 md:mb-6 drop-shadow-2xl text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-purple-400 leading-tight tracking-tight px-4">
              Where Artists and Opportunities Meet
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl font-semibold mb-6 md:mb-8 drop-shadow-lg text-gray-100 leading-relaxed px-4">
              Connect. Create. Get Discovered.
            </p>

            {/* Call-to-action buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6 md:mb-8 px-4">
              <Link
                href="/signup"
                className="w-full sm:w-auto px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-blue-600 to-violet-600 text-white font-bold text-base md:text-lg rounded-xl shadow-xl hover:from-blue-500 hover:to-violet-500 transition-all duration-300 transform hover:scale-105 whitespace-nowrap text-center"
                aria-label="Join Indiestry and create your account"
                onClick={() => trackEvent('landing_page_cta_clicked', { action: 'signup', location: 'hero' })}
              >
                Join Now
              </Link>
              <Link
                href="/explore"
                className="w-full sm:w-auto px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-white/20 via-white/30 to-white/20 border border-white/30 text-white font-bold text-base md:text-lg rounded-xl shadow-xl hover:from-white/30 hover:via-white/40 hover:to-white/30 hover:text-blue-100 transition-all duration-300 transform hover:scale-105 whitespace-nowrap backdrop-blur-sm text-center"
                aria-label="Explore Indiestry platform"
                onClick={() => trackEvent('landing_page_cta_clicked', { action: 'explore', location: 'hero' })}
              >
                Explore Indiestry
              </Link>
            </div>

            {/* Tagline */}
            <p className="text-sm md:text-base text-gray-300 drop-shadow-md leading-relaxed px-4">
              Built by Artists, for Artists
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section
        id="features"
        className="px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-border-subtle bg-surface-1 text-text-primary"
      >
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-14">
            <span className="text-mono font-mono uppercase text-accent">Everything you need</span>
            <h2 className="mt-2 mb-1.5 text-4xl md:text-5xl leading-[1.05] font-semibold tracking-[-0.025em]">
              Everything you need to{' '}
              <em className={`italic font-bold text-[44px] md:text-[56px] leading-[1.05] inline-block ${ITALIC_GRADIENT}`}>
                succeed.
              </em>
            </h2>
            <p className="text-text-secondary text-base max-w-[540px] mx-auto">
              From voice acting opportunities to community building, Indiestry provides the
              tools and connections you need.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featureCards.map((c) => (
              <FeatureCard
                key={c.title}
                icon={c.icon}
                title={c.title}
                body={c.body}
                detail={c.detail}
                badge={c.badge}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        id="final-cta"
        className="relative overflow-hidden px-4 sm:px-6 lg:px-8 py-24 md:py-32 border-t border-border-subtle bg-surface-0 text-text-primary"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_700px_360px_at_50%_50%,oklch(0.62_0.22_270/0.22),transparent_70%)]"
        />
        <div
          aria-hidden
          className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 opacity-[0.18]"
        >
          <Waveform count={28} />
        </div>
        <div
          aria-hidden
          className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.18] scale-x-[-1]"
        >
          <Waveform count={28} />
        </div>
        <div className="relative max-w-[720px] mx-auto text-center">
          <span className="text-mono font-mono uppercase text-accent">Ready when you are</span>
          <h2 className="my-3 text-4xl md:text-[56px] leading-[1.05] font-semibold tracking-[-0.025em]">
            Your story starts{' '}
            <span
              className={`italic font-bold text-5xl md:text-[64px] leading-[1.05] ${ITALIC_GRADIENT}`}
            >
              here.
            </span>
          </h2>
          <p className="mt-2 mb-8 text-text-secondary text-base leading-[1.6]">
            Join Indiestry today and take your voice acting career to the next level.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              href="/signup"
              onClick={() =>
                trackEvent('landing_page_cta_clicked', {
                  action: 'signup',
                  location: 'final_cta',
                })
              }
            >
              <DsButton
                variant="primary"
                size="md"
                className={`!h-14 !px-7 !text-[15px] ${ACCENT_GLOW_LG}`}
              >
                Sign up for free
                <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.5} />
              </DsButton>
            </Link>
            <Link
              href="/explore"
              onClick={() =>
                trackEvent('landing_page_cta_clicked', {
                  action: 'explore',
                  location: 'final_cta',
                })
              }
              className="inline-flex h-14 items-center px-4 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
            >
              Explore first
            </Link>
          </div>
          <p className="mt-6 text-text-tertiary text-xs">
            No credit card. No agent fees. No catch.
          </p>
        </div>
      </section>

      {/* Footer (inlined here so the shared Footer.tsx — used on 35+ other pages — stays untouched) */}
      <footer className="bg-surface-1 border-t border-border-subtle text-text-primary">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-8">
            <div>
              <Link
                href="/"
                className="flex items-center gap-2.5 mb-3 hover:opacity-90 transition-opacity"
              >
                <Image
                  src="/indiestrylogofull.png"
                  alt="Indiestry"
                  width={28}
                  height={28}
                  className="rounded-full"
                />
                <span className="text-base font-semibold tracking-tight text-text-primary">
                  Indiestry
                </span>
              </Link>
              <p className="text-text-secondary text-[13px] leading-[1.6] max-w-[240px]">
                Where artists and opportunities meet. Built by voice actors, for voice
                actors.
              </p>
            </div>

            <div>
              <h3 className="text-mono font-mono uppercase text-accent mb-3.5">Navigate</h3>
              <ul className="list-none p-0 m-0 flex flex-col gap-2">
                {FOOTER_NAVIGATE.map((l) => (
                  <li key={l.label}>
                    <Link
                      href={l.href}
                      className="text-text-secondary text-[13px] no-underline hover:text-text-primary transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-mono font-mono uppercase text-accent mb-3.5">Resources</h3>
              <ul className="list-none p-0 m-0 flex flex-col gap-2">
                {FOOTER_RESOURCES.map((l) => (
                  <li key={l.label}>
                    <Link
                      href={l.href}
                      className="text-text-secondary text-[13px] no-underline hover:text-text-primary transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-mono font-mono uppercase text-accent mb-3.5">Legal</h3>
              <ul className="list-none p-0 m-0 flex flex-col gap-2">
                {FOOTER_LEGAL.map((l) => (
                  <li key={l.label}>
                    <Link
                      href={l.href}
                      className="text-text-secondary text-[13px] no-underline hover:text-text-primary transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-border-subtle flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <span className="text-text-tertiary text-xs">
              © 2026 Indiestry. All rights reserved.
            </span>
            <span className="text-mono font-mono uppercase text-text-tertiary">
              Built by artists, for artists.
            </span>
            {SHOW_SOCIAL_LINKS && (
              <div className="flex gap-3.5">
                {[
                  { label: 'x', href: 'https://x.com' },
                  { label: 'instagram', href: 'https://instagram.com' },
                  { label: 'youtube', href: 'https://youtube.com' },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-text-tertiary lowercase hover:text-text-primary transition-colors"
                  >
                    {s.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
